import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';

import NewsletterForm from './NewsletterForm';
import { useNewsletterForm } from '@/hooks/useNewsletterForm';
import { usePersona } from '@/hooks/usePersona';

/**
 * Hero-style newsletter block — cream-50 background, navy text, lead-magnet
 * image on the right. Used at the top of the Home page and as the masthead
 * of the dedicated /newsletter page.
 *
 * @param {object} props
 * @param {string} [props.image]                  Optional lead-magnet illustration.
 * @param {string} [props.imageAlt='']
 * @param {string} [props.source='banner']        Beehiiv UTM source.
 * @param {string} [props.leadMagnet]
 * @param {string} [props.className]
 *
 * @example
 *   <NewsletterBanner image="/lead/visa-guide.png" leadMagnet="visa-guide" />
 */

// T8 — newsletter headline varies by journey stage when the visitor is known
// (set via the /commencer-ici picker). Falls back to the generic i18n headline.
const INTENT_STAGE = { 'move-soon': 'decided', planning: 'research', exploring: 'dream' };
const STAGE_HEADLINE = {
  dream: { fr: 'Reçois le Panama dans ta boîte mail', en: 'Get Panama in your inbox', es: 'Recibe Panamá en tu correo' },
  research: { fr: 'Le guide hebdo pour préparer ton départ', en: 'The weekly guide to plan your move', es: 'La guía semanal para planear tu mudanza' },
  decided: { fr: "Ton copilote d'installation, chaque semaine", en: 'Your weekly relocation copilot', es: 'Tu copiloto semanal de mudanza' },
};

export default function NewsletterBanner({
  image,
  imageAlt = '',
  source = 'banner',
  leadMagnet,
  className = '',
}) {
  const { t, i18n } = useTranslation('components');
  const form = useNewsletterForm({ source, leadMagnet });
  const reduce = useReducedMotion();
  const { intentSlug } = usePersona();
  const lang = ['fr', 'en', 'es'].includes(i18n.language) ? i18n.language : 'fr';
  const stage = INTENT_STAGE[intentSlug];
  const headline = stage ? STAGE_HEADLINE[stage][lang] : t('newsletter.headline');

  return (
    <motion.section
      initial={reduce ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-120px' }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`overflow-hidden rounded-card bg-cream-50 shadow-card ${className}`}
      aria-labelledby="nl-banner-title"
    >
      <div className="grid grid-cols-1 items-center gap-6 p-6 md:grid-cols-[1fr_auto] md:gap-10 md:p-10">
        <div>
          <p className="v4-eyebrow mb-3">{t('newsletter.cta')}</p>
          <h2 id="nl-banner-title" className="font-serif text-3xl font-bold italic text-navy-900 md:text-4xl">
            {headline}
          </h2>
          <p className="mt-3 max-w-xl text-pretty text-gray-700">{t('newsletter.body')}</p>
          <div className="mt-5 max-w-md">
            <NewsletterForm form={form} />
          </div>
        </div>
        {image && (
          <img
            src={image}
            alt={imageAlt}
            width={220}
            height={280}
            loading="lazy"
            decoding="async"
            className="hidden h-auto w-44 rounded-card object-cover shadow-card md:block lg:w-56"
          />
        )}
      </div>
    </motion.section>
  );
}
